import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MessageCircle, Search, BellOff, Check, X, Users } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import {
    getConversations,
    seedUserDataOnce,
    getRequests,
    acceptRequest,
    declineRequest,
    isMuted,
    isPeerOnline,
} from "@/lib/socialStore";
import { MOCK_PEOPLE } from "@/lib/mockData";
import { getJoinedEvents } from "@/lib/socialStore";
import { getAllEvents, listEventsByOwner } from "@/lib/entitiesStore";
import { useSocialVersion, timeAgo } from "@/lib/hooks";
import InitialAvatar from "@/components/InitialAvatar";

const TABS = [
    { id: "chats",    label: "Chats" },
    { id: "requests", label: "Requests" },
    { id: "events",   label: "Event Chats" },
];

const Inbox = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const v = useSocialVersion();
    const [params] = useSearchParams();
    const initialTab = params.get("tab") === "requests" ? "requests" : params.get("tab") === "events" ? "events" : "chats";
    const [tab, setTab] = useState(initialTab);

    useEffect(() => {
        if (user) seedUserDataOnce(user.id, MOCK_PEOPLE);
    }, [user]);

    const conversations = useMemo(
        () => (user ? getConversations(user.id) : []),
        [user, v]
    );
    const requests = useMemo(
        () => (user ? getRequests(user.id) : []),
        [user, v]
    );
    const eventChats = useMemo(() => {
        if (!user) return [];
        const all = getAllEvents();
        const joinedIds = new Set(getJoinedEvents(user.id));
        const hostingIds = new Set(listEventsByOwner(user.id).map((e) => e.id));
        return all.filter((e) => joinedIds.has(e.id) || hostingIds.has(e.id));
    }, [user, v]);

    const totalUnread = conversations.reduce((s, c) => s + (c.unread || 0), 0);

    return (
        <div className="min-h-full animate-fade-in" data-testid="messages-screen">
            <header className="sticky top-0 z-10 px-5 pt-[max(14px,env(safe-area-inset-top))] pb-3 bg-background/90 backdrop-blur border-b border-border/60">
                <div className="flex items-center justify-between">
                    <h1 className="font-display text-[22px] font-semibold tracking-tight">
                        Messages
                    </h1>
                    <button
                        data-testid="messages-search-btn"
                        onClick={() => navigate("/search")}
                        className="h-10 w-10 flex items-center justify-center rounded-xl bg-secondary hover:bg-muted active:scale-95"
                        aria-label="Search"
                    >
                        <Search size={18} />
                    </button>
                </div>
                {/* Tabs */}
                <div className="mt-3 flex gap-2 overflow-x-auto no-scrollbar" data-testid="messages-tabs">
                    {TABS.map((t) => {
                        const count =
                            t.id === "chats" ? totalUnread :
                            t.id === "requests" ? requests.length :
                            eventChats.length;
                        const isActive = tab === t.id;
                        return (
                            <button
                                key={t.id}
                                data-testid={`messages-tab-${t.id}`}
                                onClick={() => setTab(t.id)}
                                className={[
                                    "shrink-0 h-9 px-3.5 rounded-full text-[12.5px] font-medium inline-flex items-center gap-1.5 transition-colors",
                                    isActive
                                        ? "bg-primary text-primary-foreground"
                                        : "bg-secondary text-secondary-foreground hover:bg-muted",
                                ].join(" ")}
                            >
                                {t.label}
                                {count > 0 && (
                                    <span
                                        className={[
                                            "min-w-[18px] h-[18px] px-1 rounded-full text-[10.5px] font-semibold inline-flex items-center justify-center",
                                            isActive
                                                ? "bg-primary-foreground/25 text-primary-foreground"
                                                : "bg-primary/15 text-primary",
                                        ].join(" ")}
                                    >
                                        {count}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>
            </header>

            {tab === "chats" && (
                conversations.length === 0
                    ? <EmptyState icon={MessageCircle} title="No messages yet" subtitle="Say hi to someone on Neyra to start a conversation." />
                    : (
                        <ul className="px-3 py-2" data-testid="inbox-list">
                            {conversations.map((c) => {
                                const muted = user ? isMuted(user.id, "direct", c.id) : false;
                                const online = isPeerOnline(c.peerId);
                                return (
                                    <li key={c.id}>
                                        <button
                                            data-testid={`inbox-item-${c.id}`}
                                            onClick={() => navigate(`/messages/${c.id}`)}
                                            className="w-full flex items-center gap-3 p-3 rounded-2xl hover:bg-accent/40 active:scale-[0.99] transition-[transform,background-color] duration-150 text-left"
                                        >
                                            <div className="relative">
                                                <InitialAvatar name={c.peerName} size={48} />
                                                {online && (
                                                    <span
                                                        data-testid={`inbox-online-${c.id}`}
                                                        className="absolute bottom-0 right-0 h-3 w-3 rounded-full bg-emerald-500 ring-2 ring-background"
                                                        aria-label="Online"
                                                    />
                                                )}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center justify-between gap-3">
                                                    <span className="font-display text-[14.5px] font-semibold truncate inline-flex items-center gap-1.5">
                                                        {c.peerName}
                                                        {muted && (
                                                            <BellOff
                                                                size={12}
                                                                className="text-muted-foreground shrink-0"
                                                                data-testid={`inbox-muted-${c.id}`}
                                                                aria-label="Muted"
                                                            />
                                                        )}
                                                    </span>
                                                    <span className="text-[11px] text-muted-foreground shrink-0">
                                                        {timeAgo(c.updatedAt)}
                                                    </span>
                                                </div>
                                                <div className="flex items-center justify-between gap-3 mt-0.5">
                                                    <span
                                                        className={[
                                                            "text-[12.5px] truncate",
                                                            c.unread > 0 ? "text-foreground font-medium" : "text-muted-foreground",
                                                        ].join(" ")}
                                                    >
                                                        {c.lastMessage}
                                                    </span>
                                                    {c.unread > 0 && !muted && (
                                                        <span
                                                            data-testid={`inbox-unread-${c.id}`}
                                                            className="shrink-0 min-w-[20px] h-5 px-1.5 rounded-full bg-primary text-primary-foreground text-[10.5px] font-semibold flex items-center justify-center"
                                                        >
                                                            {c.unread}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    )
            )}

            {tab === "requests" && (
                requests.length === 0
                    ? <EmptyState icon={MessageCircle} title="No requests" subtitle="Messages from people you don't follow will appear here." />
                    : (
                        <ul className="px-3 py-2 space-y-2" data-testid="requests-list">
                            {requests.map((r) => (
                                <li
                                    key={r.id}
                                    data-testid={`request-${r.id}`}
                                    className="p-3 rounded-2xl border border-border bg-card shadow-soft"
                                >
                                    <div className="flex items-center gap-3">
                                        <button
                                            onClick={() => navigate(`/u/${r.peerUsername}`)}
                                            className="shrink-0"
                                            aria-label="Open profile"
                                        >
                                            <InitialAvatar name={r.peerName} size={44} />
                                        </button>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-display text-[14px] font-semibold truncate">{r.peerName}</div>
                                            <div className="text-[11.5px] text-muted-foreground truncate">@{r.peerUsername}</div>
                                            <div className="mt-1 text-[12.5px] text-foreground/85 truncate">{r.message}</div>
                                        </div>
                                    </div>
                                    <div className="mt-3 grid grid-cols-2 gap-2">
                                        <button
                                            data-testid={`request-accept-${r.id}`}
                                            onClick={() => {
                                                const convId = acceptRequest(user.id, r.id);
                                                if (convId) navigate(`/messages/${convId}`);
                                            }}
                                            className="h-10 rounded-full text-primary-foreground inline-flex items-center justify-center gap-1.5 text-[12.5px] font-medium active:scale-[0.98]"
                                            style={{
                                                background: "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.78))",
                                            }}
                                        >
                                            <Check size={13} />
                                            Accept
                                        </button>
                                        <button
                                            data-testid={`request-decline-${r.id}`}
                                            onClick={() => declineRequest(user.id, r.id)}
                                            className="h-10 rounded-full bg-secondary border border-border hover:bg-muted inline-flex items-center justify-center gap-1.5 text-[12.5px] font-medium active:scale-[0.98]"
                                        >
                                            <X size={13} />
                                            Decline
                                        </button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )
            )}

            {tab === "events" && (
                eventChats.length === 0
                    ? <EmptyState icon={Users} title="No event chats yet" subtitle="Join an event to access its group chat." />
                    : (
                        <ul className="px-3 py-2" data-testid="event-chats-list">
                            {eventChats.map((e) => {
                                const muted = user ? isMuted(user.id, "event", e.id) : false;
                                return (
                                    <li key={e.id}>
                                        <button
                                            data-testid={`event-chat-item-${e.id}`}
                                            onClick={() => navigate(`/events/${e.id}/chat`)}
                                            className="w-full flex items-center gap-3 p-3 rounded-2xl hover:bg-accent/40 active:scale-[0.99] transition-[transform,background-color] duration-150 text-left"
                                        >
                                            <span className="h-12 w-12 rounded-xl overflow-hidden bg-muted shrink-0 flex items-center justify-center">
                                                {e.image ? (
                                                    <img src={e.image} alt="" className="h-full w-full object-cover" />
                                                ) : (
                                                    <Users size={18} className="text-primary" />
                                                )}
                                            </span>
                                            <div className="flex-1 min-w-0">
                                                <div className="font-display text-[14.5px] font-semibold truncate inline-flex items-center gap-1.5">
                                                    {e.title}
                                                    {muted && (
                                                        <BellOff
                                                            size={12}
                                                            className="text-muted-foreground shrink-0"
                                                            data-testid={`event-chat-muted-${e.id}`}
                                                            aria-label="Muted"
                                                        />
                                                    )}
                                                </div>
                                                <div className="text-[12px] text-muted-foreground truncate">
                                                    {e.date} · {e.location}
                                                </div>
                                            </div>
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    )
            )}
        </div>
    );
};

const EmptyState = ({ icon: Icon, title, subtitle }) => (
    <div className="pt-20 text-center px-6" data-testid="inbox-empty">
        <div
            className="mx-auto h-14 w-14 rounded-full flex items-center justify-center"
            style={{
                background:
                    "radial-gradient(120% 120% at 30% 20%, hsl(var(--primary) / 0.22), hsl(var(--primary) / 0.06) 60%, transparent 100%)",
            }}
        >
            <Icon size={22} className="text-primary" />
        </div>
        <p className="mt-4 font-display text-[15px] font-semibold">{title}</p>
        <p className="text-[12.5px] text-muted-foreground mt-1">{subtitle}</p>
    </div>
);

export default Inbox;
